import { useState, useRef, useEffect } from 'react';
import { Loader2, Mail, MessageSquareMore, NotepadText } from 'lucide-react';
import { SendType } from 'pages/Conversation/types';

interface SplitButtonProps {
  onMainAction: () => void;
  onOptionSelect: (option: SendType) => void;
  buttonText: string;
  contact: any;
  isSendingMessage: boolean;
}

const SplitButton = ({ onMainAction, onOptionSelect, buttonText, contact, isSendingMessage }: SplitButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleMainClick = () => {
    if (onMainAction) {
      onMainAction();
    }
  };

  const handleOptionClick = (option: SendType) => {
    if (onOptionSelect) {
      onOptionSelect(option);
      setIsOpen(false);
    }
  }


  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };
  return (
    <div ref={dropdownRef} className="relative inline-block text-left">
      <div className="flex">
        {/* Main Button */}
        <button
          onClick={handleMainClick}
          className="w-10 inline-flex items-center px-2 py-2 text-sm font-medium text-white bg-custom-600 border border-custom-600 rounded-l-md hover:bg-custom-700 focus:outline-none"
        >
          {isSendingMessage ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : buttonText === "SMS" ? (
            <MessageSquareMore className="w-4 h-4" />
          ) : buttonText === "Email" ? (
            <Mail className="w-4 h-4" />
          ) : (
            <NotepadText className="w-4 h-4" />
          )}
        </button>

        {/* Dropdown Toggle */}
        <button
          onClick={toggleDropdown}
          className="inline-flex items-center px-2 py-2 text-sm font-medium text-white bg-custom-600 border border-l-0 border-custom-600 rounded-r-md hover:bg-custom-700 focus:outline-none"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={isOpen ? "M19 9l-7 7-7-7" : "M5 15l7-7 7 7"}
            />
          </svg>
        </button>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 m-auto w-40 bottom-[42px] origin-top-right bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="py-1">
            <button
              disabled={contact?.phoneNumber === null || contact?.phoneNumber === ''}
              onClick={() => handleOptionClick(SendType.SMS)}
              className={`flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100 ${contact?.phoneNumber === null || contact?.phoneNumber === '' ? 'text-gray-400' : 'text-gray-700'
                }`}
            >
              <MessageSquareMore className="w-4 h-4 mr-2" />
              Send SMS
            </button>
            <button
              disabled={contact?.email === null || contact?.email === ''}
              onClick={() => handleOptionClick(SendType.Email)}
              className={`flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100 ${contact?.email === null || contact?.email === '' ? 'text-gray-400' : 'text-gray-700'
                }`}
            >
              <Mail className="w-4 h-4 mr-2" />
              Send Email
            </button>
            <button
              onClick={() => handleOptionClick(SendType.SystemNote)}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 text-left hover:bg-gray-100"
            >
              <NotepadText className="w-4 h-4 mr-2" />
              Send Internal
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SplitButton;